import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Breadcrumb from '../../general/Breadcrumb/Breadcrumb'
import BackButton from '../../general/Button/predefined/BackButton'
import Text from '../../general/Typography/Text/Text'
import { Card } from '../../layout'
import { useUiKitContext } from '../../utils/UiKitProvider/UiKitProvider'

export type BasicPageHeaderProps = {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
  onBack?: () => void
  backButtonHref?: string
  breadcrumbItems?: JSX.Element[]
  breadcrumb?: {
    text: string
    onClick: () => void
  }
  className?: string
  onBackTitle?: string
  testId?: string
}

const BasicPageHeader = (props: BasicPageHeaderProps) => {
  const {
    title,
    subtitle,
    children,
    breadcrumbItems,
    breadcrumb,
    onBack,
    backButtonHref,
    className,
    onBackTitle,
    testId
  } = props
  const { translation: t } = useUiKitContext()
  const shouldRenderBackButton = onBack || backButtonHref

  const renderBackButton = (
    <BackButton onClick={onBack} data-testid={`${testId}-back-button`}>
      {onBackTitle || t('general.buttons.back')}
    </BackButton>
  )

  return (
    <Wrapper data-testid={testId}>
      <Content className={className}>
        <div>
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <BreadcrumbStyled>
              {breadcrumbItems.map((item, index) => {
                if (!item.key) {
                  throw new Error(
                    `A breadcrumb item does not have a key prop. Please provide one. Index: ${index}, Item: ${item}`
                  )
                }

                return <Breadcrumb.Item key={item.key}>{item}</Breadcrumb.Item>
              })}
            </BreadcrumbStyled>
          )}
          {breadcrumb && (
            <BreadcrumbStyled>
              <Breadcrumb.Item>
                <BreadcrumbClickableText onClick={breadcrumb.onClick}>
                  {breadcrumb.text} /
                </BreadcrumbClickableText>
              </Breadcrumb.Item>
            </BreadcrumbStyled>
          )}
          {shouldRenderBackButton && backButtonHref && (
            <Link to={backButtonHref}>{renderBackButton}</Link>
          )}
          {shouldRenderBackButton && !backButtonHref && renderBackButton}
          <TitleWrapper>{title}</TitleWrapper>
          <TextStyled>{subtitle}</TextStyled>
        </div>
        {children}
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color9};
`

const Content = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    gap: 1rem;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`
const TextStyled = styled(Text)`
  display: block;

  /* neutral.color3 (#333333) at 70% — applied as an alpha colour rather than
     opacity so an inline link in the subtitle isn't dimmed by a parent fade. */
  color: rgba(51, 51, 51, 0.7);
` as typeof Text

const BreadcrumbStyled = styled(Breadcrumb)`
  margin-bottom: 4px;
`

const BreadcrumbClickableText = styled.div`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-size: 1rem;
  cursor: pointer;
  display: inline;

  &:hover {
    color: ${({ theme }) => theme.color.neutral.color3};
  }
`

export default BasicPageHeader
